/**
 * PRIVATE REBELS V5 — theme.js
 * Version: 5.0.0
 * V4 Audit Fixes Applied:
 * - FIX 1: Est. 2024 removed — modal pre controlled by settings
 * - FIX 2: SocialProof module removed — no fake purchase events
 * - FIX 3: Press fabrications cleared — use real coverage only
 * - FIX 4: Provenance div guarded — only renders with content
 * - FIX 5: Wishlist href fixed — routes to /pages/wishlist
 * - FIX 6: show_recently_viewed added to settings
 * - FIX 7: welcome_modal_pre added to settings
 * - FIX 8: BreadcrumbList schema added to theme.liquid
 */

(function() {
  'use strict';

  /* ── CUSTOM CURSOR ── */
  const CursorModule = {
    init() {
      if (window.matchMedia('(hover: none)').matches) return;
      const c = document.getElementById('cur');
      const r = document.getElementById('ring');
      if (!c || !r) return;
      document.addEventListener('mousemove', e => {
        c.style.left = e.clientX + 'px';
        c.style.top  = e.clientY + 'px';
        r.style.left = e.clientX + 'px';
        r.style.top  = e.clientY + 'px';
      });
      document.addEventListener('mouseover', e => {
        if (e.target.closest('a,button')) {
          r.style.transform = 'translate(-50%,-50%) scale(2.2)';
        }
      });
      document.addEventListener('mouseout', () => {
        r.style.transform = 'translate(-50%,-50%) scale(1)';
      });
    }
  };

  /* ── LOADER & PAGE CURTAIN ── */
  const LoaderModule = {
    dismissed: false,
    dismiss() {
      if (this.dismissed) return;
      this.dismissed = true;

      setTimeout(() => {
        const curtain = document.getElementById('pageCurtain');
        if (curtain) curtain.classList.add('done');
      }, 200);

      setTimeout(() => {
        const loader = document.getElementById('loader');
        if (loader) loader.classList.add('out');
      }, 2100);

      setTimeout(() => {
        if (!sessionStorage.getItem('pr_welcomed_v5')) {
          const overlay = document.getElementById('welcomeOverlay');
          if (overlay) overlay.classList.add('active');
        }
      }, 2600);

      setTimeout(() => {
        if (!localStorage.getItem('pr_cookies_v5')) {
          const bar = document.getElementById('cookieBar');
          if (bar) bar.classList.add('show');
        }
      }, 1500);
    },
    init() {
      // Dismiss on load, or after 4s max — whichever comes first
      window.addEventListener('load', () => this.dismiss());
      setTimeout(() => this.dismiss(), 4000);
    }
  };

  /* ── WELCOME MODAL ── */
  const WelcomeModal = {
    dismiss() {
      sessionStorage.setItem('pr_welcomed_v5', '1');
      const overlay = document.getElementById('welcomeOverlay');
      if (!overlay) return;
      overlay.classList.remove('active');
      setTimeout(() => overlay.setAttribute('aria-hidden', 'true'), 700);
    }
  };
  window.dismissWelcome = () => WelcomeModal.dismiss();

  // Handle welcome modal email submit — captures email then dismisses
  window.handleWelcomeSubmit = function() {
    const input = document.getElementById('welcomeEmailInput');
    const btn   = document.querySelector('.welcome-enter');
    const email = input ? input.value.trim() : '';

    // If no email entered, just dismiss (respects skip behaviour)
    if (!email || !email.includes('@')) {
      WelcomeModal.dismiss();
      return;
    }

    // Capture email to Shopify customer list → syncs to Klaviyo
    if (btn) { btn.textContent = '...'; btn.disabled = true; }

    fetch('/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        form_type: 'customer',
        utf8: '✓',
        'contact[email]': email,
        'contact[tags]': 'newsletter,early-access,modal-signup'
      })
    })
    .then(() => {
      if (btn) { btn.textContent = 'You\'re In ✓'; }
      setTimeout(() => WelcomeModal.dismiss(), 900);
    })
    .catch(() => {
      // Still dismiss even on error — do not trap user in modal
      WelcomeModal.dismiss();
    });
  };

  /* ── HEADER SCROLL BEHAVIOR ── */
  const HeaderModule = {
    init() {
      const header = document.getElementById('siteHeader');
      if (!header) return;
      window.addEventListener('scroll', () => {
        header.classList.toggle('stuck', window.scrollY > 36);
      }, { passive: true });
    }
  };

  /* ── TOAST NOTIFICATIONS ── */
  const Toast = {
    timer: null,
    show(msg) {
      const toast = document.getElementById('toast');
      const tt    = document.getElementById('tt');
      if (!toast || !tt) return;
      tt.textContent = msg;
      toast.classList.add('on');
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => toast.classList.remove('on'), 3000);
    }
  };
  window.showToast = (msg) => Toast.show(msg);

  /* ── CART DRAWER (Shopify Ajax Cart API) ── */
  const CartDrawer = {
    open() {
      document.getElementById('cartDraw')?.classList.add('on');
      document.getElementById('veil')?.classList.add('on');
      document.body.style.overflow = 'hidden';
      this.refresh();
    },
    close() {
      document.getElementById('cartDraw')?.classList.remove('on');
      document.getElementById('veil')?.classList.remove('on');
      document.body.style.overflow = '';
    },

    // Add item by variant ID via Shopify Ajax API
    async addItem(variantId, quantity) {
      quantity = quantity || 1;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: variantId, quantity })
        });
        if (!res.ok) {
          const err = await res.json();
          Toast.show(err.description || 'Could not add item');
          return;
        }
        await this.refresh();
        Toast.show('Added to bag');
        this.open();
      } catch(e) {
        Toast.show('Something went wrong');
      }
    },

    // Remove a line item by its key
    async removeItem(key) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        await this.refresh();
      } catch(e) {
        Toast.show('Could not remove item');
      }
    },

    // Fetch cart state from Shopify and re-render
    async refresh() {
      try {
        const res  = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const cart = await res.json();
        this.render(cart);
        this.updateBadge(cart.item_count);
        this.updateShippingBar(cart.total_price / 100);
      } catch(e) {}
    },

    render(cart) {
      const body  = document.querySelector('.db');
      const total = document.getElementById('cartTotal');
      if (!body) return;

      if (total) {
        const currency = window.PRIV?.currency || 'USD';
        total.textContent = new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cart.total_price / 100);
      }

      if (cart.item_count === 0) {
        body.innerHTML = `<div class="empty-st"><div class="empty-st-h">Nothing Yet</div><div class="empty-st-s">Your bag is empty</div></div>`;
        return;
      }

      body.innerHTML = cart.items.map(item => {
        const img = item.image
          ? `<img src="${item.image}" alt="${item.title}" style="width:100%;height:100%;object-fit:cover;"/>`
          : `<span style="font-family:var(--font-display);font-size:1.4rem;">${item.title[0]}</span>`;
        const variantLabel = item.variant_title && item.variant_title !== 'Default Title'
          ? `<div class="cart-item-variant">${item.variant_title}</div>` : '';
        const currency = window.PRIV?.currency || 'USD';
        const price = new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(item.final_line_price / 100);
        const key   = encodeURIComponent(item.key);
        return `
          <div class="cart-item">
            <div class="cart-item-img">${img}</div>
            <div>
              <div class="cart-item-name">${item.product_title}</div>
              ${variantLabel}
            </div>
            <div style="display:flex;flex-direction:column;align-items:flex-end;gap:8px">
              <div class="cart-item-price">${price}</div>
              <button class="cart-item-remove" onclick="CartDrawer.removeItem('${key}')" aria-label="Remove ${item.product_title}">✕</button>
            </div>
          </div>
        `;
      }).join('');
    },

    updateBadge(count) {
      const badge = document.getElementById('bagCount');
      if (!badge) return;
      badge.textContent = count;
      badge.classList.toggle('empty', count === 0);
      if (count > 0) {
        badge.classList.add('bump');
        setTimeout(() => badge.classList.remove('bump'), 300);
      }
    },

    updateShippingBar(totalAmount) {
      const bar = document.querySelector('.sp-f');
      const lbl = document.querySelector('.sp-l');
      if (!bar) return;
      const target   = window.PRIV?.freeShippingThreshold || 80;
      const currency = window.PRIV?.currency || 'USD';
      const pct      = Math.min(100, (totalAmount / target) * 100);
      bar.style.width = pct + '%';
      if (lbl) {
        if (totalAmount >= target) {
          lbl.textContent = 'Free Shipping Unlocked ✓';
        } else {
          const rem = (target - totalAmount).toFixed(2);
          const sym = new Intl.NumberFormat('en', { style: 'currency', currency }).formatToParts(0).find(p => p.type === 'currency')?.value || '$';
          lbl.textContent = `${sym}${rem} away from free shipping`;
        }
      }
    }
  };
  window.CartDrawer = CartDrawer;
  window.openCart  = () => CartDrawer.open();
  window.closeCart = () => CartDrawer.close();

  /* ── MOBILE MENU ── */
  const MobileMenu = {
    open() {
      document.getElementById('mobileOverlay')?.classList.add('active');
      document.body.style.overflow = 'hidden';
      document.querySelector('.hamburger')?.setAttribute('aria-expanded', 'true');
    },
    close() {
      document.getElementById('mobileOverlay')?.classList.remove('active');
      document.body.style.overflow = '';
      document.querySelector('.hamburger')?.setAttribute('aria-expanded', 'false');
    }
  };
  window.openMobileMenu  = () => MobileMenu.open();
  window.closeMobileMenu = () => MobileMenu.close();

  /* ── SIZE SELECTION ── */
  // Resolve variant ID from current color + size selections
  function resolveVariantId(card) {
    const map       = JSON.parse(card.dataset.variantMap || '{}');
    const colorBtn  = card.querySelector('.p-color-swatch.on');
    const sizeBtn   = card.querySelector('.sz.on');
    const color     = colorBtn?.dataset.color || '';
    const size      = sizeBtn?.dataset.size   || '';
    const key       = color + '__' + size;
    return map[key] || sizeBtn?.dataset.variantId || null;
  }

  window.selectSize = function(btn) {
    const siblings = btn.closest('.szs')?.querySelectorAll('.sz');
    siblings?.forEach(s => s.classList.remove('on'));
    btn.classList.add('on');
  };

  window.selectColor = function(btn) {
    const card = btn.closest('.prod');
    // Mark active swatch
    card?.querySelectorAll('.p-color-swatch').forEach(s => s.classList.remove('on'));
    btn.classList.add('on');
    // Swap card image if color has its own image
    const img = card?.querySelector('.prod-main-img');
    if (img && btn.dataset.img && btn.dataset.img !== 'null') {
      img.src = btn.dataset.img;
    }
    // Update each size button's variant-id for this color
    const map = JSON.parse(card?.dataset.variantMap || '{}');
    card?.querySelectorAll('.sz').forEach(sz => {
      const key = btn.dataset.color + '__' + sz.dataset.size;
      if (map[key]) sz.dataset.variantId = map[key];
    });
  };

  /* ── ADD TO CART (homepage card quick-add) ── */
  window.quickAdd = function(btn) {
    const sizeBtn = btn.closest('.prod')?.querySelector('.sz.on');
    if (!sizeBtn) {
      Toast.show('Please select a size');
      return;
    }
    const variantId = sizeBtn.dataset.variantId;
    if (!variantId) {
      Toast.show('Please select a size');
      return;
    }
    CartDrawer.addItem(variantId, 1);
  };

  /* ── NEWSLETTER ── */
  const Newsletter = {
    submit(btn) {
      const inp = btn.previousElementSibling;
      if (!inp || !inp.value || !inp.value.includes('@')) {
        Toast.show('Please enter a valid email');
        inp?.focus();
        return;
      }
      // POST to Shopify customer newsletter endpoint (no backend required)
      const email = inp.value.trim();
      fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          form_type: 'customer',
          utf8: '✓',
          'contact[email]': email,
          'contact[tags]': 'newsletter'
        })
      }).then(() => {
        btn.textContent = 'Joined ✓';
        btn.disabled = true;
        inp.value = '';
        Toast.show('Welcome to the Inner Circle');
      }).catch(() => {
        Toast.show('Could not subscribe — try again');
      });
    }
  };
  window.joinNewsletter = (btn) => Newsletter.submit(btn);

  /* ── COOKIE CONSENT ── */
  window.acceptCookies = function() {
    localStorage.setItem('pr_cookies_v5', 'accepted');
    document.getElementById('cookieBar')?.classList.remove('show');
  };
  window.declineCookies = function() {
    localStorage.setItem('pr_cookies_v5', 'declined');
    document.getElementById('cookieBar')?.classList.remove('show');
  };

  /* ── REVEAL ON SCROLL ── */
  const RevealModule = {
    init() {
      const obs = new IntersectionObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('v'); });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
      document.querySelectorAll('.r').forEach(el => obs.observe(el));
    }
  };

  /* ── ACTIVE NAV ON SCROLL ── */
  const ActiveNav = {
    init() {
      const sections  = document.querySelectorAll('section[id], div[id]');
      const navLinks  = document.querySelectorAll('.nav-links a');
      if (!sections.length || !navLinks.length) return;
      const obs = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            navLinks.forEach(a => {
              a.classList.toggle('active', a.getAttribute('href') === '#' + e.target.id);
            });
          }
        });
      }, { threshold: 0.4 });
      sections.forEach(s => obs.observe(s));
    }
  };

  /* ── KEYBOARD ACCESSIBILITY ── */
  const KeyboardModule = {
    init() {
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape') {
          if (document.getElementById('welcomeOverlay')?.classList.contains('active')) WelcomeModal.dismiss();
          if (document.getElementById('cartDraw')?.classList.contains('on')) CartDrawer.close();
          if (document.getElementById('mobileOverlay')?.classList.contains('active')) MobileMenu.close();
        }
      });
    }
  };

  /* ── RECENTLY VIEWED (requires show_recently_viewed setting) ── */
  const RecentlyViewed = {
    STORAGE_KEY: 'pr_recently_viewed',
    MAX: 8,
    track(handle) {
      if (!handle) return;
      let items = this.get();
      items = [handle, ...items.filter(h => h !== handle)].slice(0, this.MAX);
      try { localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items)); } catch(e) {}
    },
    get() {
      try { return JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '[]'); } catch(e) { return []; }
    }
  };
  window.RecentlyViewed = RecentlyViewed;

  /* ── STICKY ATC (product page) ── */
  const StickyATC = {
    init() {
      const atc = document.querySelector('.sticky-atc');
      const productInfo = document.querySelector('.product-info');
      if (!atc || !productInfo) return;
      const obs = new IntersectionObserver(([entry]) => {
        atc.classList.toggle('visible', !entry.isIntersecting);
      }, { threshold: 0 });
      obs.observe(productInfo);
    }
  };

  /* ── PRODUCT GALLERY THUMBNAILS ── */
  const ProductGallery = {
    init() {
      const thumbs = document.querySelectorAll('.product-thumb');
      const main   = document.querySelector('.product-gallery-main img');
      if (!thumbs.length || !main) return;
      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          thumbs.forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
          const src = thumb.querySelector('img')?.src;
          if (src) main.src = src;
        });
      });
    }
  };

  /* ── INIT ── */
  document.addEventListener('DOMContentLoaded', () => {
    CursorModule.init();
    LoaderModule.init();
    HeaderModule.init();
    RevealModule.init();
    ActiveNav.init();
    KeyboardModule.init();
    StickyATC.init();
    ProductGallery.init();

    // Sync cart badge with real Shopify cart on every page load
    CartDrawer.refresh();

    // Track current product
    if (window.PRIV?.productHandle) {
      RecentlyViewed.track(window.PRIV.productHandle);
    }

    console.log(
      '%cPRIVATE REBELS — FINAL%c\n' +
      'OG tags: ADDED\nShipping bar: DYNAMIC (PRIV)\nCurrency: USD fallback\n' +
      'Newsletter: WIRED to /contact\nContact form: Shopify native POST\n' +
      'freeShippingThreshold: BRIDGED from settings\n',
      'color:#b91c1c;font-family:monospace;font-size:14px;font-weight:bold;',
      'color:#4ade80;font-family:monospace;font-size:11px;'
    );
  });

})();
